import Axios from "axios";
import { loadingScreen } from "./Global";
import Cookies from "js-cookie";
import { refreshAccessToken } from "./Auth";
import { showNotificationWithIcon } from "./Notification";
import i18n from "./i18next";
import { store } from "../index"
import { changePopupVisibility } from "../ui/GeneralComponents/LoginPopup/LoginPopupAction"
import { ECHO_URL } from "../../src/config"

const activeRequests = new Map();

Axios.defaults.headers.post["Content-Type"] = "application/json";
Axios.defaults.headers.put["Content-Type"] = "application/json";
Axios.defaults.headers.delete["Content-Type"] = "application/json";

function axiosCallsThen(successFunc, response, preventStopLoading) {
  if (preventStopLoading !== true) {
    loadingScreen(false);
  }
  
  if (successFunc) {
    return successFunc(response);
  }
}

function axiosCallsCatch(errorFunc, response, callLogoutOn401, retryCallback, url, preventStopLoading) {
  if (preventStopLoading !== true) {
    loadingScreen(false);
  }

  /** if response cancelled by user */
  if (response && response.__CANCEL__) {
    return;
  }
 
  const isResponse401 = response.response && response.response.status === 401;
  
  if (isResponse401 && handleUnAuthorizedRequest(url, callLogoutOn401, undefined, retryCallback)) {
    return;
  }

  let errorHeader = i18n.t("SomethingWentWrong");
  const { message, isDescription } = extractErrorMessage(response);

  if (errorFunc) {
    errorFunc(message, isDescription);
  } else {
    showNotificationWithIcon(errorHeader, message, "error");
  }
}

/**
 * Prepare requests configs.
 * Adds header.
 * Adds cancel token if exist.
 * Adds data if exist.
 * @param {*} header
 * @param {*} cancelToken
 * @param {*} data
 */
const prepareRequestConfig = (
  header,
  cancelToken,
  responseType = "json",
  data = undefined,
  excludeAuthorizationHeader = undefined
) => {
  let config = {
    headers: header,
    responseType: responseType
  };

  if (!excludeAuthorizationHeader) {
    const authToken = Cookies.get("ORA_BIPS_NQID");
    if (authToken) {
      const authHeaderValue =  `Bearer ${authToken}`

      if (config.headers) {
        config.headers["Authorization"] = authHeaderValue
      }
      else {
        config.headers = {Authorization : authHeaderValue}
      }
    }
  }

  // header is already prepered return config
  if (header) return config

  if (cancelToken) config.cancelToken = cancelToken;
  
  if (data) config.data = data;
  
  return config;
};

/**
 * 
 * @param {*} url 
 * @param {*} callLogoutOn401 
 * @param {*} errorFunc 
 * @param {*} retryCallback 
 * @returns if unauthorized is handled 
 * shows login popup or if slider active refreshes token 
 */
function handleUnAuthorizedRequest(url, callLogoutOn401, errorFunc, retryCallback) {
  const isDashboardSliderActive = sessionStorage.getItem("currentSlider") !== null;
  const showLoginPopup =  url !== ECHO_URL && callLogoutOn401 === true;
  const loginWithRefreshToken = url !== ECHO_URL && isDashboardSliderActive;
  
  loadingScreen(false)

  if (loginWithRefreshToken) {
    refreshAccessToken(retryCallback);
    return true;

  } else if (showLoginPopup) {
    store.dispatch(changePopupVisibility(true, retryCallback));
    return true;
  }

  if (errorFunc) {
    errorFunc();
  }

  return false;
}

/**
 * 
 * @param {*} response 
 * @returns error message and isDescription property from response  
 */
function extractErrorMessage(response) {
  let isDescription = false;
  let message = response?.message;

  const data = response?.response?.data;
  if (data) {
    if (data.error_description) {
      isDescription = true;
      message = data.error_description;
    } else if (data.message) {
      message = data.message;
    } else if (typeof data === "string") {
      message = data;
    }
  }

  return { message, isDescription };
}


export const get = (
  url,
  successFunc,
  errorFunc = undefined,
  showLoadingScreen = true,
  excludeAuthorizationHeader = false,
  header = undefined,
  callLogoutOn401 = true,
  cancelToken = undefined,
  preventStopLoading = false,
  responseType = "json",
  cancelId = undefined
) => {
  if (showLoadingScreen) {
    loadingScreen(showLoadingScreen);
  }

  let source = undefined;
  if (cancelId) {
    if (activeRequests.has(cancelId)) {
      activeRequests.get(cancelId).cancel();
    }
    source = Axios.CancelToken.source();
    activeRequests.set(cancelId, source);
    cancelToken = source.token;
  }

  const config = prepareRequestConfig(header, cancelToken, responseType, undefined, excludeAuthorizationHeader);
  
  const isUnAuthorized = !excludeAuthorizationHeader && (!config.headers || !config.headers["Authorization"]);
 
  const retryCallback = function callback() {
    get(url,
      successFunc,
      errorFunc,
      showLoadingScreen = true,
      excludeAuthorizationHeader = false,
      header = undefined,
      callLogoutOn401 = true,
      cancelToken = undefined)
  };
 
  if (isUnAuthorized && !handleUnAuthorizedRequest(url, callLogoutOn401, errorFunc, retryCallback)) {
     return;
  }
  
  return Axios.get(url, config)
    .then(response => axiosCallsThen(successFunc, response, preventStopLoading))
    .catch(response => axiosCallsCatch(errorFunc, response, callLogoutOn401, retryCallback, url, preventStopLoading))
    .finally(() => {
      if (cancelId && activeRequests.get(cancelId) === source) {
        activeRequests.delete(cancelId);
      }
    });
};

export const post = (
  url,
  requestBody,
  successFunc,
  errorFunc = undefined,
  showLoadingScreen = true,
  excludeAuthorizationHeader = false,
  header = undefined,
  callLogoutOn401 = true,
  cancelToken = undefined,
  preventStopLoading = false,
  responseType = "json",
  cancelId = undefined
) => {
  if (showLoadingScreen) {
    loadingScreen(showLoadingScreen);
  }

  let source = undefined;
  if (cancelId) {
    if (activeRequests.has(cancelId)) {
      activeRequests.get(cancelId).cancel();
    }
    source = Axios.CancelToken.source();
    activeRequests.set(cancelId, source);
    cancelToken = source.token;
  }

  const config = prepareRequestConfig(
    header,
    cancelToken,
    responseType,
    undefined,
    excludeAuthorizationHeader,
  );

  const isUnAuthorized = !excludeAuthorizationHeader && (!config.headers || !config.headers["Authorization"]);
  
  const retryCallback  = (function callback() {
    return post(url,
      requestBody,
      successFunc,
      errorFunc,
      showLoadingScreen = true,
      excludeAuthorizationHeader = false,
      header = undefined,
      callLogoutOn401 = true,
      cancelToken = undefined)
  });

  if (isUnAuthorized) {
    return handleUnAuthorizedRequest(url,callLogoutOn401, errorFunc, retryCallback);
  }
  
  return Axios.post(url, requestBody, config)
    .then(response => axiosCallsThen(successFunc, response, preventStopLoading))
    .catch(response => axiosCallsCatch(errorFunc, response, callLogoutOn401, retryCallback , url, preventStopLoading))
    .finally(() => {
      if (cancelId && activeRequests.get(cancelId) === source) {
        activeRequests.delete(cancelId);
      }
    });
};

export const put = (
  url,
  requestBody,
  successFunc,
  errorFunc = undefined,
  excludeAuthorizationHeader = false,
  header,
  callLogoutOn401 = true,
  showLoadingScreen = true,
  cancelToken = undefined,
  preventStopLoading = false,
  responseType = "json",
  cancelId = undefined
) => {
  if (showLoadingScreen) {
    loadingScreen(showLoadingScreen);
  }

  let source = undefined;
  if (cancelId) {
    if (activeRequests.has(cancelId)) {
      activeRequests.get(cancelId).cancel();
    }
    source = Axios.CancelToken.source();
    activeRequests.set(cancelId, source);
    cancelToken = source.token;
  }

  const config = prepareRequestConfig(header, cancelToken, responseType, undefined, excludeAuthorizationHeader);
  
  const isUnAuthorized = !excludeAuthorizationHeader && (!config.headers || !config.headers["Authorization"]);
 
  const retryCallback = function callback() {
    put(url,
      requestBody,
      successFunc,
      errorFunc,
      excludeAuthorizationHeader = false,
      header,
      callLogoutOn401 = true,
      showLoadingScreen = true,
      cancelToken = undefined)
  }
  if (isUnAuthorized) {
    return handleUnAuthorizedRequest(url,callLogoutOn401, errorFunc, retryCallback);
  }
  
  return Axios.put(url, requestBody, config)
    .then(response => axiosCallsThen(successFunc, response, preventStopLoading))
    .catch(response => axiosCallsCatch(errorFunc, response, callLogoutOn401, retryCallback, url, preventStopLoading))
    .finally(() => {
      if (cancelId && activeRequests.get(cancelId) === source) {
        activeRequests.delete(cancelId);
      }
    });
};

export const del = (
  url,
  requestBody,
  successFunc,
  errorFunc = undefined,
  excludeAuthorizationHeader = false,
  header,
  callLogoutOn401 = true,
  showLoadingScreen = true,
  cancelToken = undefined,
  preventStopLoading = false,
  responseType = "json",
  cancelId = undefined
) => {
  if (showLoadingScreen) {
    loadingScreen(showLoadingScreen);
  }

  let source = undefined;
  if (cancelId) {
    if (activeRequests.has(cancelId)) {
      activeRequests.get(cancelId).cancel();
    }
    source = Axios.CancelToken.source();
    activeRequests.set(cancelId, source);
    cancelToken = source.token;
  }

  const data = excludeAuthorizationHeader? null : requestBody;
  const config = prepareRequestConfig(header, cancelToken, responseType, data, excludeAuthorizationHeader);
  const isUnAuthorized = !excludeAuthorizationHeader && (!config.headers || !config.headers["Authorization"]);
  const retryCallback = function callback() {
    del(url,
      requestBody,
      successFunc,
      errorFunc,
      excludeAuthorizationHeader = false,
      header,
      callLogoutOn401 = true,
      showLoadingScreen = true,
      cancelToken = undefined)
  }

  if (isUnAuthorized) {
    return handleUnAuthorizedRequest(url,callLogoutOn401, errorFunc, retryCallback);
  }


  return Axios.delete(url, config)
    .then(response => axiosCallsThen(successFunc, response, preventStopLoading))
    .catch(response => axiosCallsCatch(errorFunc, response, callLogoutOn401, retryCallback, url, preventStopLoading))
    .finally(() => {
      if (cancelId && activeRequests.get(cancelId) === source) {
        activeRequests.delete(cancelId);
      }
    });
};

